import { useProgress } from "@react-three/drei";
import { useEffect, useRef, useState } from "react";
import { useContent } from "../hooks/useContent";
import { MeridianSplash } from "./ui/MeridianSplash";

type Props = {
  forceVisible?: boolean;
};

// The branded splash plays once per session. Internal navigation can remount
// this component (e.g. /reviews -> /decks crosses RouteFade groups and rebuilds
// the scene subtree); without this guard the white overlay would flash again
// even though assets are already cached.
let splashPlayed = false;

export function Loader({ forceVisible = false }: Props = {}) {
  const { active, progress } = useProgress();
  const contentQuery = useContent();
  // Settled = no longer pending (success or error). On error the splash still
  // clears so the ContentError state can render underneath.
  const contentSettled = !contentQuery.isPending;
  const [visible, setVisible] = useState(() => forceVisible || !splashPlayed);
  const dismissed = useRef(false);

  useEffect(() => {
    if (forceVisible) return;
    if (!active && progress === 100 && contentSettled && !dismissed.current) {
      const id = setTimeout(() => {
        dismissed.current = true;
        splashPlayed = true;
        setVisible(false);
      }, 600);
      return () => clearTimeout(id);
    }
  }, [active, progress, forceVisible, contentSettled]);

  if (!visible) return null;

  const done = !forceVisible && !active && progress === 100 && contentSettled;

  return <MeridianSplash fading={done} />;
}
